import { useQuery } from '@tanstack/react-query';
import { Building2, Users, Mail, Activity } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { QueueProcessorHealthCard } from './QueueProcessorHealthCard';

export function InfrastructurePanel() {
  const { data: workspaceCount, isLoading: loadingWorkspaces } = useQuery({
    queryKey: ['admin', 'infrastructure', 'workspaces'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('workspaces')
        .select('*', { count: 'exact', head: true });
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: userCount, isLoading: loadingUsers } = useQuery({
    queryKey: ['admin', 'infrastructure', 'users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('user_id');
      if (error) throw error;
      const uniqueUsers = new Set(data?.map((m) => m.user_id));
      return uniqueUsers.size;
    },
  });

  const { data: activeCampaignCount, isLoading: loadingCampaigns } = useQuery({
    queryKey: ['admin', 'infrastructure', 'campaigns'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('hermes_email_campaigns')
        .select('*', { count: 'exact', head: true })
        .in('status', ['active', 'sending']);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const isLoading = loadingWorkspaces || loadingUsers || loadingCampaigns;

  return (
    <div className="space-y-6">
      <SheetHeader>
        <SheetTitle>Infrastructure</SheetTitle>
        <SheetDescription>System overview and health</SheetDescription>
      </SheetHeader>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Workspaces</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : workspaceCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : userCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : activeCampaignCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">OK</div>
          </CardContent>
        </Card>
      </div>

      <QueueProcessorHealthCard />
    </div>
  );
}
